import { useRef, useState } from 'react'
import { PanelError } from '@/console/api/errors'
import { requestConfirmGlobalInbound } from '@/console/data/globalInboundRollback'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { Note } from '@/console/ui/Note'
import { Stack } from '@/console/ui/Stack'
import {
  GLOBAL_SHIPPER_CODE,
  GLOBAL_WAREHOUSE_CODE,
  RECONFIRM_REQUEST,
  SERVER_MAY_STILL_BE_PROCESSING_MESSAGE,
  interpretReconfirmError,
} from './globalRollback'
import styles from './GlobalRollback.module.css'

type GlobalReconfirmSectionProps = {
  /** 롤백의 원장 복원과 확정의 후보 조회가 같은 인덱스 범위를 잠가 동시에 돌면 실패한다(§6.5). */
  rollbackExecuting: boolean
  /** 롤백한 문서가 생긴 뒤 아직 재확정하지 않았는지. */
  reconfirmNeeded: boolean
  /** 롤백 실행이 응답 없이 끝나 서버가 아직 롤백 중일 수 있다 — 재확정이 겹치지 않게 다음 판정까지 잠근다. */
  serverMayStillBeProcessing: boolean
  onReconfirmed: () => void
  onSubmittingChange: (submitting: boolean) => void
}

const useGlobalReconfirm = ({ onReconfirmed, onSubmittingChange }: Pick<GlobalReconfirmSectionProps, 'onReconfirmed' | 'onSubmittingChange'>) => {
  const [submitting, setSubmitting] = useState(false)
  const [createdInboundPlanIds, setCreatedInboundPlanIds] = useState<number[] | null>(null)
  const [reconfirmError, setReconfirmError] = useState<PanelError | null>(null)
  const inFlight = useRef(false)

  // 리렌더 전에 들어온 두 번째 호출까지 막는다.
  const submit = (): void => {
    if (inFlight.current) return
    inFlight.current = true
    setSubmitting(true)
    onSubmittingChange(true)
    setReconfirmError(null)
    requestConfirmGlobalInbound(RECONFIRM_REQUEST)
      .then((createdInboundPlanIds) => {
        setCreatedInboundPlanIds(createdInboundPlanIds)
        onReconfirmed()
      })
      .catch((error) => {
        setCreatedInboundPlanIds(null)
        setReconfirmError(interpretReconfirmError(error))
      })
      .finally(() => {
        inFlight.current = false
        setSubmitting(false)
        onSubmittingChange(false)
      })
  }

  return { submitting, createdInboundPlanIds, error: reconfirmError, submit }
}

const ReconfirmResult = ({ createdInboundPlanIds }: { createdInboundPlanIds: number[] | null }): JSX.Element | null => {
  if (!createdInboundPlanIds) {
    return null
  }

  if (createdInboundPlanIds.length === 0) {
    return <Callout>확정할 후보가 없어 새 입고지시가 만들어지지 않았어요.</Callout>
  }

  return (
    <Callout tone="good">
      생성된 입고지시 {createdInboundPlanIds.length}건: <span className={styles.planIds}>{createdInboundPlanIds.join(', ')}</span>
    </Callout>
  )
}

const RECONFIRM_SCOPE_NOTICE = '재확정은 되돌린 문서만이 아니라 지금 확정 가능한 주문 전체를 새 컷에 담아요.'

const ReconfirmScopeNote = (): JSX.Element => (
  <Note>
    화주 {GLOBAL_SHIPPER_CODE} · 창고 {GLOBAL_WAREHOUSE_CODE}(신여주Global). <b>MUSINSAGLOBAL</b> 화주 쓰기 권한 필요. 롤백 실행 중에는 누를 수
    없어요.
  </Note>
)

type ReconfirmConfirmDialogProps = { open: boolean; onConfirm: () => void; onCancel: () => void }

const ReconfirmConfirmDialog = ({ open, onConfirm, onCancel }: ReconfirmConfirmDialogProps): JSX.Element => (
  <ConfirmDialog
    open={open}
    title="재확정할까요?"
    body={<p style={{ margin: 0 }}>{RECONFIRM_SCOPE_NOTICE} 되돌린 건 외에 다른 후보가 있으면 함께 확정돼요.</p>}
    confirmLabel="재확정"
    danger
    onConfirm={onConfirm}
    onCancel={onCancel}
  />
)

/** 재확정 — 롤백으로 되살아난 주문을 새 입고문서로 확정한다. 컷 일자·차수는 백엔드가 채번한다(§3.5). */
export const GlobalReconfirmSection = ({
  rollbackExecuting,
  reconfirmNeeded,
  serverMayStillBeProcessing,
  onReconfirmed,
  onSubmittingChange,
}: GlobalReconfirmSectionProps): JSX.Element => {
  const [pendingConfirm, setPendingConfirm] = useState(false)
  const reconfirm = useGlobalReconfirm({ onReconfirmed, onSubmittingChange })
  const reconfirmLocked = rollbackExecuting || serverMayStillBeProcessing || reconfirm.submitting
  // 확인 다이얼로그가 열린 사이 롤백이 시작될 수 있어 누른 시점에 다시 확인한다.
  const confirmReconfirm = (): void => {
    setPendingConfirm(false)
    if (reconfirmLocked) return
    reconfirm.submit()
  }

  return (
    <Stack>
      {reconfirmNeeded ? <Callout tone="crit">롤백한 문서가 있어요. 재확정을 빠뜨리면 주문이 입고요청 상태로 남아요.</Callout> : null}
      {serverMayStillBeProcessing ? <Callout tone="warn">{SERVER_MAY_STILL_BE_PROCESSING_MESSAGE}</Callout> : null}
      <Callout tone="warn">{RECONFIRM_SCOPE_NOTICE}</Callout>
      <div>
        <Button disabled={reconfirmLocked} onClick={() => setPendingConfirm(true)}>
          {reconfirm.submitting ? '재확정 중…' : '재확정'}
        </Button>
      </div>
      {reconfirm.error ? <Callout tone="crit">{reconfirm.error.message}</Callout> : null}
      <ReconfirmResult createdInboundPlanIds={reconfirm.createdInboundPlanIds} />
      <ReconfirmScopeNote />
      <ReconfirmConfirmDialog open={pendingConfirm} onConfirm={confirmReconfirm} onCancel={() => setPendingConfirm(false)} />
    </Stack>
  )
}
